<template>
    <md-card class="ql-cue" v-if="visible" :style="styles" :id="cue.id">
        <project-cues-cue-header 
            :expanded="expanded"
            :isEditing="isEditing"
            :isEditingDescription="isEditingDescription"
            :cue="cue"
            :layer="layer"
            :layers="editableLayers" 
            :locked="locked"
            :editable="editable"
            :color="color"
            :totalPages="totalPages"
            :isFloatingCue="isFloatingCue"
            @toggle="toggle"
            @cueUpdated="updatedCue"
            @updateStatus="updateStatus"
            @tetherCue="tetherCue"
            @untetherCue="untetherCue"
            ref="ql-cue-header"
        />
    </md-card>
</template>

<script>
import { mapGetters } from 'vuex';
import vueDragResize from 'vue-draggable-resizable';
/**
 * Component For...
 */
import ProjectCuesCueHeader from '@/components/print/cues/cue-header';
import ProjectCuesCueBody from '@/components/print/cues/cue-body';

const ProjectCuesCue = {
    name: 'projectCuesCue',
    props: ['cue', 'open', 'locked', 'totalPages', 'cuesListWidth', 'cueIndex', 'svgContentWidth', 'landscape'],
    data: function() {
        return {
            expanded: this.open,
            isEditing: false,
            isEditingDescription: false,
            isDragging: false,
            originWidth: 450,
            draggingX: 0,
            draggingY: 0,
            tethering: false,
            handler: 0,
            selectedScale: localStorage.getItem('page-print-scale') || 1
        };
    },
    watch: {
        isFloatingCue() {
            this.handler++;
        },
        floatingX() {
            if (!this.isDragging) {
                this.handler++;
            }
        },
        floatingY() {
            if (!this.isDragging) {
                this.handler++;
            }
        }
    },
    computed: {
        ...mapGetters([
            'userProjectData',
            'titleFontSizeOptions',
            'layerByCue',
            'editableLayers',
            'floatingCues',
            'cuePlacement',
            'getDefaultCuePlacementPrint',
            'getPrintCuePlacement'
        ]),
        layer() {
            return this.layerByCue(this.cue);
        },
        isFloatingCue() {
            return Object.keys(this.floatingCues).includes(this.cue.id);
        },
        floatingX() {
            return this.isFloatingCue ? this.floatingCues[this.cue.id].left * this.selectedScale : 0;
        },
        floatingY() {
            return this.isFloatingCue ? this.floatingCues[this.cue.id].top * this.selectedScale : 0;
        },
        visible() {
            let visible = true;
            
            if (this.layer) {
                if (this.layer.hidden === true) {
                    visible = false;
                }
            }
            return visible;
        },
        editable() {
            return false;
        },
        color() {
            let val = 'white';
            if (this.layer) {
                val = this.layer.color;
            }
            return val;
        },
        titleFontSize() {
            if (this.titleFontSizeOptions[this.cue.id]) {
                return this.titleFontSizeOptions[this.cue.id];
            }
            if (this.cue.titleFontSize) {
                return this.cue.titleFontSize;
            }
            const layers = this.userProjectData.layers;
            if (layers && layers[this.layer.id]) {
                if (layers[this.layer.id].titleFontSize) {
                    return layers[this.layer.id].titleFontSize;
                }
            }
            return this.layer.titleFontSize || 14;
        },
        styles() {
            const cuePlaceHeight = (Number(((this.titleFontSize)) + 2).toFixed(5)) * this.selectedScale;
            const defaultCuePlacement = this.getPrintCuePlacement(this.layer.id);
            let cueMarginTop = 0;
            if (this.selectedScale >= 0.5 && this.selectedScale <= 1.51) {
                cueMarginTop = 1;
            } else if (this.selectedScale >= 1.52 && this.selectedScale <= 2.51) {
                cueMarginTop = 0.92;
            } else if (this.selectedScale >= 2.52 && this.selectedScale <= 3.01) {
                cueMarginTop = 0.9;
            } else if (this.selectedScale >= 3.02 && this.selectedScale <= 4.01) {
                cueMarginTop = 0.85;
            }
            const styles = {
                borderColor: this.color,
                borderWidth: `${Number(1 / this.selectedScale).toFixed(5)}px`,
                top: `${this.cue.offsetY + ((this.cue.type === 'position' ? (this.cue.height / 2) : this.cue.height) * this.selectedScale)}px`,
                zIndex: (this.expanded ? 100 : 11),
                transform: `scale(${this.selectedScale})`,
                transformOrigin: `left ${Number(((this.cue.titleFontSize || this.layer.titleFontSize || 14) * 1.1) + 2).toFixed(5)}px`,
                marginTop: `${0 - ((cuePlaceHeight - (cuePlaceHeight * (this.selectedScale - cueMarginTop))))}px`,
                height: (this.expanded ? 'auto' : `${cuePlaceHeight / this.selectedScale}px`),
                width: 'auto'
            };
            if (this.isFloatingCue) {
                styles.position = 'absolute';
                styles.left = (this.expanded ? 'auto' : `${this.floatingX}px`);
                styles.top = `${this.floatingY + (12 * this.selectedScale)}px`;
                styles.transformOrigin = `left ${Number(((this.titleFontSize) * 1.1 * this.selectedScale) + 2).toFixed(5)}px`;
            } else if (defaultCuePlacement === 'leftEdge') {
                styles.left = '2px';
                styles.transform = `translateX(calc(-${this.selectedScale * 100}% - 12px)) scale(${this.selectedScale})`;
                styles.transformOrigin = `left ${Number((((this.titleFontSize) * 1.1 * this.selectedScale) + 2) + (this.selectedScale * 1)).toFixed(5)}px`;
            } else if (defaultCuePlacement === 'rightEdge') {
                styles.left = 'auto';
                styles.right = '2px';
                styles.transform = `translateX(calc(${this.selectedScale * 100}% + 12px)) scale(${this.selectedScale})`;
                styles.transformOrigin = `right ${Number((((this.titleFontSize) * 1.1 * this.selectedScale) + 2) + (this.selectedScale * 1)).toFixed(5)}px`;
            } else if (defaultCuePlacement === 'right') {
                styles.left = 'auto';
                styles.right = '2px';
                styles.transformOrigin = `right ${Number((((this.titleFontSize) * 1.1 * this.selectedScale) + 2) + (this.selectedScale * 1)).toFixed(5)}px`;
            } else if (defaultCuePlacement === 'left') {
                styles.left = '2px';
                styles.transformOrigin = `left ${Number((((this.titleFontSize) * 1.1 * this.selectedScale) + 2) + (this.selectedScale * 1)).toFixed(5)}px`;
            }
            return styles;
        }
    },
    methods: {
        toggle() {
            if (this.$props.cue) {
                if (this.expanded) {
                    this.contract();
                } else {
                    this.expand();
                }
            }
        },
        expand() {
            this.expanded = true;
            this.$nextTick(() => {
                const bounds = this.$el.getBoundingClientRect();
                this.cue.height = bounds.height;
                this.$emit('openCue', this.cue);
            });
        },
        contract() {
            this.expanded = false;
            this.$emit('closeCue', this.cue);
        },
        destroy() {
            this.$emit('destroy', this.$props.cue);
        },
        updatedCue(cue) {
            this.$emit('updatedCue', cue);
        },
        updateStatus({isEditing = false, isEditingDescription = false}) {
            this.isEditing = isEditing;
            this.isEditingDescription = isEditingDescription;
        },
        onDrag(left, top) {
            if (this.tethering) {
                this.isDragging = false;
                return false;
            }
            this.floatingCues[this.cue.id].left = left;
            this.floatingCues[this.cue.id].top = top;
            this.isDragging = true;
            return true;
        },
        onDragStop(left, top) {
            this.isDragging = false;
            if (!this.tethering) {
                this.$store.dispatch('addFloatingCue', {
                    id: this.cue.id,
                    left: Math.max(left, 0),
                    top: Math.max(top, 0)
                });
            }
        },
        tetherCue() {
            this.tethering = true;
        },
        untetherCue() {
            this.tethering = false;
        }
    },
    components: {
        'project-cues-cue-body': ProjectCuesCueBody,
        'project-cues-cue-header': ProjectCuesCueHeader,
        'vue-draggable-resizable': vueDragResize
    }
};

export default ProjectCuesCue;
</script>

<style lang="scss">
.ql-print-cue-list {
    .ql-cue {
        position: absolute;
        margin-bottom: 5px;
        top: 0;
        box-sizing: border-box;
        border: 1px solid #000;
        border-radius: 4px;
        overflow: hidden;
        .md-card {
            width: 100%;
        }

        &.cue-editing, &.cue-expanded {
            width: 100% !important;
        }
    }
}
.md-theme-default .ql-print-cue-list {
    background-color: white;
}
.md-theme-show-mode .ql-print-cue-list .ql-cue {
    background-color: #424242;
}
</style>


// WEBPACK FOOTER //
// cue.vue?e86944a2